import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";

/** A cancellable list request; a late response cannot overwrite a newer one. */
export const useApiList = (path, enabled = true, withCredentials = false, onUnauthorized) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const controller = useRef(null);

  const reload = useCallback(async () => {
    if (!enabled) return;
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    setLoading(true);
    setError(false);
    try {
      const { data } = await axios.get(`${API}${path}`, { withCredentials, signal: request.signal });
      if (!request.signal.aborted) setItems(data);
    } catch (err) {
      if (request.signal.aborted) return;
      setError(true);
      if ([401, 403].includes(err.response?.status)) onUnauthorized?.();
    } finally {
      if (!request.signal.aborted) setLoading(false);
    }
  }, [path, enabled, withCredentials, onUnauthorized]);

  useEffect(() => {
    if (enabled) reload();
    else {
      setItems([]);
      setLoading(false);
      setError(false);
    }
    return () => controller.current?.abort();
  }, [enabled, reload]);

  return { items, setItems, loading, error, reload };
};